/**
 * Bullets (Phase 59, the action clock): fast projectiles stepped every tick. Pure.
 *
 * A bullet leaves the muzzle along the unit's aim (heading tilted up by its aim pitch,
 * plus a small seeded spread), then flies under the same N-body gravity as everything
 * else (BULLET_GRAVITY_SCALE) — at BULLET_SPEED it reads flat at duel range; slow it and
 * it arcs like a missile, with no new code. It is swept in sub-steps of BULLET_SUBSTEP so
 * it cannot pass through a ridge or a unit between ticks, hits the exact terrain surface,
 * and damages the first unit whose hitbox it crosses (armour applies, as for a beam).
 */

import { hitLocation, locationMultiplier } from './hitLocation';
import { throughArmour } from './armour';
import { drives } from './body';
import { abovePoint } from './los';
import type { HitLocation, Bullet, GameState, PlanetState, PlayerState, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize, vecLength, vecCross } from '../math';
import { dsin, dcos, dexp } from './dmath';
import { groundMaterial } from './groundMaterial';
import { GROUND_BULLETS, GROUND_BULLET_FLOOR, GROUND_WEAR } from '../constants/ground';
import { wearId, wearCount, bumpWear } from './groundWear';
import { simWork } from './workCounters';
import { gravityAccel, checkCollision } from './physics';
import { getPlayerWorldPosition } from './gameState';
import { applyHitscanDamage } from './hitscan';
import { HITBOXES } from '../constants/hitboxes';
import { PERSON_BODY_HEIGHT } from './scaleProfile';
import type { HitPart } from '../types/body';
import { planetShapes, shapesNear, firstHit, firstMovingHit, segmentBlocked, type PlanetShapes } from './hits';
import { firstShipHit, shipBlow } from './shipDamage';
import { friendlyShare, applyDamage, structureMaterial, damageStructure } from './damage';
import { zonesOn, zoneOf, zoneTableOf, seatBehind, hullShare, knockedOut } from './vehicleZones';
import { ROUND_PENETRATION, ZONE_RULES } from '../constants/vehicleZones';
import { seatUnit } from './seats';
import { layoutFor } from './modules';
import { moduleBlow } from './moduleDamage';
import { personSetOf, type PersonSet } from './scaleProfile';
import { personPointAt, personGroundHit, unitPointAt, digSegmentHit, type GroundDials } from './fineTerrain';
import { stanceSpheres, stanceCentreHeight, eyeScaleOf, bodyStanceOf } from './stance';
import { leanOffset, leanPart } from './lean';
import { weaponSpec, weaponOf, windShare, type WeaponSpec } from './weapons';
import { dragKeep } from './ballistics';
import { dronePos } from './drones';
import { supplyDroneTargets, hitSupplyDrone } from './supplyDrone';
import { SeededRandom, keyedRandom, hashKey } from './random';
import { planetFoliage, foliageNear, foliageCrossed, type PlanetFoliage } from './foliage';
import { jungleCrossed } from './jungleDepth';
/** 67.61: the jungle field's cells' keys for the shots' draws, apart from every tree id (forest ids are under 2^31) */
const JUNGLE_KEY = 2147483648;
import { FOLIAGE_BULLETS, ACTION_WIND, SUPPRESSION } from '../constants/action';
import { PHYS_DT } from '../constants/physics';
import { SUPPLY_DRONE } from '../constants/logistics';
import { actionWindAccel } from './wind';
import { headingOf, rightOf, turnHeading, type WalkObstacle, type ObstacleRef } from './surfaceWalk';
import {
  ACTION_EYE_HEIGHT, OUT_OF_BOUNDS,
} from '../constants';
import { defaultOn } from './defaultOn';

/** Where a unit's aim points: its heading tilted up by `pitch` (radians). */
export function aimDirection(surfaceDir: Vec3, heading: Vec3, pitch: number): Vec3 {
  const n = vecNormalize(surfaceDir);
  return vecNormalize(vecAdd(vecScale(heading, dcos(pitch)), vecScale(n, dsin(pitch))));
}

/** The muzzle: at eye height above the unit's feet — where it stands, or where it flies. */
export function muzzlePoint(p: PlayerState, planets: PlanetState[], eyeHeight: number = ACTION_EYE_HEIGHT, fine?: GroundDials): Vec3 {
  // 66.7 lean step 2: leaning out of cover, the gun is aside of where the person stands (core/lean.ts)
  const lean = p.cover ? leanOffset(p, fine?.k, eyeHeight * eyeScaleOf(p)) : null;
  if (lean) return vecAdd(muzzlePoint({ ...p, cover: undefined }, planets, eyeHeight, fine), lean);
  if (p.airborne && p.airbornePos) {
    const up = vecNormalize(vecSub(p.airbornePos, planets[p.planetIndex].position));
    return vecAdd(p.airbornePos, vecScale(up, eyeHeight));
  }
  eyeHeight *= eyeScaleOf(p);   // 62.2: a crouched or prone shooter fires from lower (216: eased through a change)
  if (p.seatPos) return abovePoint(p.seatPos, planets[p.planetIndex], eyeHeight);   // 63.1d step 2: in its hatch
  if (fine && fine.fineAmplitude > 0) return unitPointAt(planets[p.planetIndex], p.surfaceDir, fine, eyeHeight);   // S3: on the fine ground (62.57: looked up once)
  const n = vecNormalize(p.surfaceDir);
  return vecAdd(getPlayerWorldPosition(p, planets), vecScale(n, eyeHeight));
}

/** Centre of a unit's hitbox (the same sphere missiles hit): standing, or where it flies. */
/** 66.7: what person `p` shows past its cover while it leans out (core/lean.ts leanPart), on its ground; null when it does not lean. */
export function leanPartOf(p: PlayerState, planets: PlanetState[], fine: GroundDials): { p0: Vec3; p1: Vec3; r: number } | null {
  if (!p.cover?.out || fine.k === undefined) return null;
  const feet = hitboxCenter({ ...p, cover: undefined, stance: undefined }, planets, 0, fine);
  return leanPart(p, fine.k, feet, vecNormalize(p.surfaceDir));
}

export function hitboxCenter(p: PlayerState, planets: PlanetState[], centreHeight = 0.5, fine?: GroundDials & Partial<Pick<PersonSet, 'hitboxCenter' | 'hitboxRadius'>>): Vec3 {
  // 66.7 lean step 2: leaning out of cover, a shooter aims at what shows past the edge — the head and the gun's
  // shoulder (core/lean.ts leanPart), its middle
  if (p.cover && fine?.k !== undefined) {
    const part = leanPartOf(p, planets, fine);
    if (part) return vecScale(vecAdd(part.p0, part.p1), 0.5);
  }
  if (p.airborne && p.airbornePos) return p.airbornePos;
  // 62.2: a crouched or prone body is aimed at lower (its middle sphere)
  const body = bodyStanceOf(p);   // (216: the old stance's shape for the first half of a change)
  if (body && body !== 'stand' && fine?.hitboxCenter !== undefined && fine.hitboxRadius !== undefined && fine.k !== undefined) {
    centreHeight = stanceCentreHeight(body, { hitboxCenter: fine.hitboxCenter, hitboxRadius: fine.hitboxRadius, k: fine.k });
  }
  if (p.seatPos) return abovePoint(p.seatPos, planets[p.planetIndex], centreHeight);   // 63.1d step 2: in its hatch
  if (fine && fine.fineAmplitude > 0) return unitPointAt(planets[p.planetIndex], p.surfaceDir, fine, centreHeight);   // S3: on the fine ground (62.57: looked up once)
  const n = vecNormalize(p.surfaceDir);
  return vecAdd(getPlayerWorldPosition(p, planets), vecScale(n, centreHeight));
}

/**
 * Decision 132: a person's measured body in the world — the stance's capsules (constants/hitboxes.ts, in people) at
 * its feet on the ground it stands on, up its normal, along its heading; airborne, standing with its middle where the
 * flight has it.
 */
/**
 * A person's body frame in the world (decision 132's, and 65.27's hit locations read it): the stance's capsules, the feet
 * on the ground it stands on (or where its flight has its middle), its up, heading and right, and a person's height here.
 */
export function personFrame(p: PlayerState, planets: PlanetState[], ps: PersonSet): { parts: HitPart[]; P: number; feet: Vec3; n: Vec3; h: Vec3; r: Vec3 } {
  const def = HITBOXES.person;
  const body = bodyStanceOf(p);   // (216)
  const parts = p.airborne ? def.parts : body === 'crouch' ? def.stances!.crouch : body === 'prone' ? def.stances!.prone : def.parts;
  const P = PERSON_BODY_HEIGHT * ps.k;
  const n = vecNormalize(p.surfaceDir), h = headingOf(p), r = rightOf(h, n);
  let feet = p.seatPos ?? (ps.fineAmplitude > 0 ? unitPointAt(planets[p.planetIndex], p.surfaceDir, ps, 0) : getPlayerWorldPosition(p, planets));   // 63.1d step 2: or in its hatch
  if (p.airborne && p.airbornePos) {
    const c0 = parts[0] as Extract<HitPart, { shape: 'capsule' }>;
    feet = vecSub(p.airbornePos, vecScale(n, ((c0.a[1] + c0.b[1]) / 2) * P));
  }
  return { parts, P, feet, n, h, r };
}

export function measuredBody(p: PlayerState, planets: PlanetState[], ps: PersonSet): Array<{ p0: Vec3; p1: Vec3; r: number }> {
  const { parts, P, feet, n, h, r } = personFrame(p, planets, ps);
  const at = (q: [number, number, number]) => vecAdd(feet, vecAdd(vecScale(r, q[0] * P), vecAdd(vecScale(n, q[1] * P), vecScale(h, q[2] * P))));
  const body = parts.filter((q): q is Extract<HitPart, { shape: 'capsule' }> => q.shape === 'capsule').map(q => ({ p0: at(q.a), p1: at(q.b), r: q.radius * P }));
  const part = p.cover && !p.airborne ? leanPart(p, ps.k, feet, n) : null;   // 66.7: leaning out, the body at its spot and what shows past the edge
  return part ? [...body, part] : body;
}

/** Is `pt` inside person `p`'s hit volume — its measured body with `measured` (decision 132), else today's sphere? */
export function pointInPerson(pt: Vec3, p: PlayerState, planets: PlanetState[], ps: PersonSet, measured: boolean): boolean {
  if (!measured) {
    const part = p.cover ? leanPartOf(p, planets, ps) : null;   // 66.7: leaning out, the sphere at its spot and what shows past the edge
    if (part && pointInCapsule(pt, part)) return true;
    return vecLength(vecSub(pt, hitboxCenter(part ? { ...p, cover: undefined } : p, planets, ps.hitboxCenter, ps))) < ps.hitboxRadius;
  }
  return measuredBody(p, planets, ps).some(c => pointInCapsule(pt, c));
}

function pointInCapsule(pt: Vec3, c: { p0: Vec3; p1: Vec3; r: number }): boolean {
  const ax = vecSub(c.p1, c.p0), l2 = vecDot(ax, ax);
  const t = l2 > 0 ? Math.max(0, Math.min(1, vecDot(vecSub(pt, c.p0), ax) / l2)) : 0;
  return vecLength(vecSub(pt, vecAdd(c.p0, vecScale(ax, t)))) <= c.r;
}

/** Where along a→b (0…1) the segment first comes within `r` of the segment p0→p1 (a capsule), 0 if it starts inside. */
export function segmentCapsule(a: Vec3, b: Vec3, p0: Vec3, p1: Vec3, r: number): number | null {
  // a segment that misses the ball round the capsule misses the capsule: one sphere test for the people far from a round
  // (the ball a hair wider than the capsule's reach, so a graze of its far cap is never lost to rounding)
  const hx = (p1.x - p0.x) / 2, hy = (p1.y - p0.y) / 2, hz = (p1.z - p0.z) / 2;
  if (segmentSphere(a, b, { x: p0.x + hx, y: p0.y + hy, z: p0.z + hz }, (Math.sqrt(hx * hx + hy * hy + hz * hz) + r) * (1 + 1e-9)) === null) return null;
  let best = segmentSphere(a, b, p0, r);
  const t1 = segmentSphere(a, b, p1, r);
  if (t1 !== null && (best === null || t1 < best)) best = t1;
  if (best === 0) return 0;
  // the body between the caps: within r of the axis, and between its ends along it
  const ax = vecSub(p1, p0), al = vecLength(ax);
  if (al < 1e-12) return best;
  const u = vecScale(ax, 1 / al), d = vecSub(b, a), s = vecSub(a, p0);
  const sa = vecDot(s, u), da = vecDot(d, u);
  const sx = vecSub(s, vecScale(u, sa)), dx = vecSub(d, vecScale(u, da));
  const A = vecDot(dx, dx), B = 2 * vecDot(sx, dx), C = vecDot(sx, sx) - r * r;
  if (A > 1e-24) {
    const disc = B * B - 4 * A * C;
    if (disc >= 0) {
      const t = (-B - Math.sqrt(disc)) / (2 * A);
      const along = sa + t * da;
      if (t >= 0 && t <= 1 && along >= 0 && along <= al && (best === null || t < best)) best = t;
    }
  }
  if (C <= 0 && sa >= 0 && sa <= al) return 0;   // starts inside the body
  return best;
}

/** A new bullet from `shooter`'s muzzle along its aim, with a seeded spread (deterministic per shot); `origin` (69.55: a tank's barrel tip) for its start instead. */
export function fireBullet(state: GameState, shooter: PlayerState, moving = 0, spec?: WeaponSpec, extraSpread = 0, origin?: Vec3): { state: GameState; bullet: Bullet } {
  const id = state.bulletsFired ?? 0;
  const n = vecNormalize(shooter.surfaceDir);
  // 65.25a cut 2: the aim across (the player's assist: lead and wind) turns the shot, not the body
  const heading = shooter.aimYaw ? turnHeading(headingOf(shooter), n, shooter.aimYaw) : headingOf(shooter);
  const ps = personSetOf(state);
  // 65.1: the weapon in hand (the rifle is today's gun: the person set's numbers)
  const w = spec ?? weaponSpec(ps, state.config.groundWeapons === false ? 'rifle' : weaponOf(shooter), weaponOf(shooter) === 'gl40' ? { ammoReserves: false, gl40Speed: state.config.gl40Speed, gl40Gravity: state.config.gl40Gravity } : undefined);   // (65.31: the 40 mm's Lab dials)   // 65.13 / 65.14: the primary when nothing else is in hand (63.1d step 3: or the seat's gun)
  let dir = aimDirection(n, heading, shooter.aimPitch ?? 0);
  // A moving shooter's cone opens by moveSpread × how fast it moves (0 by default: today's spread exactly)
  // (63.8: a move's fire is unaimed — `extraSpread` more)
  const spread = (ps.moveSpread > 0 ? w.spread + ps.moveSpread * Math.max(0, Math.min(1, moving)) : w.spread) + extraSpread;
  if (spread > 0) {
    const rng = new SeededRandom(`${state.config.seed}-bullet-${id}`);
    const a = rng.next() * 2 * Math.PI, r = rng.next() * spread;
    const side = rightOf(heading, n);
    const up = vecNormalize(vecSub(n, vecScale(dir, vecDot(n, dir))));
    dir = vecNormalize(vecAdd(dir, vecAdd(vecScale(side, dcos(a) * r), vecScale(up, dsin(a) * r))));
  }
  const bullet: Bullet = {
    id, ownerId: shooter.id, pos: origin ?? muzzlePoint(shooter, state.planets, ps.eyeHeight, ps), vel: vecScale(dir, w.speed), age: 0, damage: w.damage,
    ...(w.life !== ps.bulletLife ? { life: w.life } : {}),
    ...(w.gravity !== undefined ? { gravity: w.gravity } : {}), ...(w.blast ? { blast: w.blast } : {}),   // S7: a rocket
    ...(w.armDistance ? { armAge: w.armDistance / w.speed } : {}),   // 65.31: the 40 mm arms this far out
    ...(w.explosion && w.explosion !== 'law' && w.explosion !== 'gl40' ? { explosion: w.explosion } : {}),   // 65.43: a new bursting round names its class (the Panzerfaust's)
    ...(w.round && (defaultOn(state.config, 'hitLocations') || state.config.armour) ? { round: w.round } : {}),   // 185: its cartridge, for what armour stops (only where it matters: the bullets as before otherwise)
    ...(w.drag ? { drag: w.drag } : {}),   // 65.30: its round's drag
    ...(w.coast ? { coast: w.coast } : {}),   // 65.46: the rocket's coast
    ...(w.penetration !== undefined ? { pen: w.penetration } : {}), ...(w.solid ? { solid: true as const } : {}),   // 69.29: the tank's solid round
  };
  return { state: { ...state, bullets: [...(state.bullets ?? []), bullet], bulletsFired: id + 1 }, bullet };
}

/** What a bullet did this tick. */
export type BulletEvent =
  | { kind: 'hitUnit'; bullet: Bullet; targetId: number; point: Vec3; damage: number; killed: boolean; /** 65.27: where it landed (with hit locations on) */ location?: HitLocation }
  | { kind: 'hitGround'; bullet: Bullet; point: Vec3; planetIndex: number }
  | { kind: 'hitCover'; bullet: Bullet; point: Vec3; planetIndex: number; /** 63.3 step 3: what stopped it */ hit?: ObstacleRef }
  | { kind: 'expired'; bullet: Bullet }
  | { kind: 'hitDrone'; bullet: Bullet; droneId: number; point: Vec3 }
  /** 69.32 step 3: a round into an enemy supply drone (core/supplyDrone.ts hitSupplyDrone) */
  | { kind: 'hitSupplyDrone'; bullet: Bullet; runId: number; point: Vec3 };

/** Distance along the segment a→b (0…1) where it first enters the sphere, or null. */
function segmentSphere(a: Vec3, b: Vec3, c: Vec3, r: number): number | null {
  // scalars, not vectors (62.49: a vector per call was a hot allocation — once per person per substep); the arithmetic
  // is vecSub / vecDot / vecLength's own, in their order, so every result is the same float
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const fx = a.x - c.x, fy = a.y - c.y, fz = a.z - c.z;
  const A = dx * dx + dy * dy + dz * dz;
  if (A < 1e-18) return Math.sqrt(fx * fx + fy * fy + fz * fz) <= r ? 0 : null;
  const B = 2 * (fx * dx + fy * dy + fz * dz);
  const C = (fx * fx + fy * fy + fz * fz) - r * r;
  if (C <= 0) return 0;   // starts inside
  const disc = B * B - 4 * A * C;
  if (disc < 0) return null;
  const t = (-B - Math.sqrt(disc)) / (2 * A);
  return t >= 0 && t <= 1 ? t : null;
}

/** Does the ball `planetNear` tests for any planet before `index` meet the segment a→b? */
function earlierBallMeets(planets: PlanetState[], index: number, a: Vec3, b: Vec3): boolean {
  for (let i = 0; i < index; i++) {
    const pl = planets[i];
    if (!pl.alive || pl.type !== 'planet') continue;
    if (segmentSphere(a, b, pl.position, pl.radius * 1.5 + 4) !== null) return true;
  }
  return false;
}

/** The planet whose cover a point could be in (within a few units of its surface), or -1. */
function planetNear(planets: PlanetState[], pt: Vec3): number {
  for (let i = 0; i < planets.length; i++) {
    const pl = planets[i];
    if (!pl.alive || pl.type !== 'planet') continue;
    if (vecLength(vecSub(pt, pl.position)) < pl.radius * 1.5 + 4) return i;
  }
  return -1;
}

/**
 * Does cover (a rock, trunk or structure) stand between `a` and `b`? The segment is tested exactly against the shapes
 * on the planets it passes (core/hits.ts, 63.3 step 3) — the same test a bullet meets, so "I can see past it" and "my
 * shot gets past it" agree. Terrain is not checked here (hasLineOfSight does that). The last two arguments are
 * today's callers' (a cache of obstacle lists, a sampling step) and no longer used: nothing is sampled.
 */
/**
 * 62.60 (the look's cost): the planet whose shapes alone `segmentCovered` would test for a→b — both ends inside its ball and
 * no earlier planet's ball touching the segment — or −1 when the segment needs the every-planet path. A caller with it may
 * keep the standing half of the test (hits.ts segmentBlockedStanding) and test the moving half fresh.
 */
export function coverPlanet(a: Vec3, b: Vec3, state: Pick<GameState, 'planets'>): number {
  const pa = planetNear(state.planets, a);
  return pa >= 0 && planetNear(state.planets, b) === pa && !earlierBallMeets(state.planets, pa, a, b) ? pa : -1;
}
export function segmentCovered(a: Vec3, b: Vec3, state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures'>, _obstaclesByPlanet?: Map<number, WalkObstacle[]>, _step?: number): boolean {
  // One planet for both ends (the layer-0 case): both inside its ball, which is convex, and no earlier planet's ball
  // touching the segment — then that planet's shapes are the only ones it can meet
  const pa = planetNear(state.planets, a);
  if (pa >= 0 && planetNear(state.planets, b) === pa && !earlierBallMeets(state.planets, pa, a, b)) return segmentBlocked(a, b, pa, state);
  for (let i = 0; i < state.planets.length; i++) {
    const pl = state.planets[i];
    if (!pl.alive || pl.type !== 'planet') continue;
    if (segmentSphere(a, b, pl.position, pl.radius * 1.5 + 4) !== null && segmentBlocked(a, b, i, state)) return true;
  }
  return false;
}

/** A person's ball, in person heights: every hit volume lies within this of its middle (tests/personBall.test.ts). */
const PERSON_BALL_PEOPLE = 1.1;
/** The radius of a person's ball (world units): PERSON_BALL_PEOPLE person heights, never under the sphere's radius. */
export function personBallRadius(ps: Pick<PersonSet, 'k' | 'hitboxRadius'>): number {
  return Math.max(PERSON_BALL_PEOPLE * PERSON_BODY_HEIGHT * ps.k, ps.hitboxRadius);
}
/**
 * The ball round person `p` that holds every hit volume it has — any stance, in the air or a seat, leaning out of cover;
 * today's sphere or decision 132's capsules: its middle (hitboxCenter with the lean set aside) and personBallRadius. A
 * segment that misses it misses the person, so the bullets' step tests it first (tests/personBall.test.ts holds it).
 */
export function personBall(p: PlayerState, planets: PlanetState[], ps: PersonSet): { c: Vec3; r: number } {
  return { c: hitboxCenter(p.cover ? { ...p, cover: undefined } : p, planets, ps.hitboxCenter, ps), r: personBallRadius(ps) };
}

export function stepBullets(state: GameState, dt: number, seconds = 0): { state: GameState; events: BulletEvent[]; near?: number[]; nearRounds?: Array<{ unitId: number; point: Vec3 }> } {
  const bullets = state.bullets ?? [];
  if (bullets.length === 0) return { state, events: [] };
  let s = state;
  const events: BulletEvent[] = [];
  const alive: Bullet[] = [];
  const nearPlanet = (pt: Vec3): number => planetNear(s.planets, pt);
  const ps = personSetOf(state);
  // 69.32 step 3: the supply drones in the air at the step's tick (their poses closed-form), each a ball SUPPLY_DRONE.hitPeople across
  const tick = Math.round(seconds / PHYS_DT), carriers = supplyDroneTargets(state, tick), carrierR = (SUPPLY_DRONE.hitPeople / 2) * PERSON_BODY_HEIGHT * ps.k;
  // Bodies do not move while bullets fly (only health changes), so each hit sphere is found once per call
  const centres = new Map<number, Vec3>();
  const centreOf = (p: PlayerState): Vec3 => { let c = centres.get(p.id); if (!c) { c = hitboxCenter(p, s.planets, ps.hitboxCenter, ps); centres.set(p.id, c); } return c; };
  // 62.2: where a segment first meets a person's hit volume — today's sphere standing (and airborne), a scaled one
  // crouched, three low spheres along the body prone; their world centres found once per call too
  const bodies = new Map<number, Array<{ c: Vec3; r: number }>>();
  // Decision 132: with `measuredBodies` a person is its measured stance capsule (constants/hitboxes.ts), not the sphere
  const measured = s.config?.measuredBodies === true;
  const capsules = new Map<number, Array<{ p0: Vec3; p1: Vec3; r: number }>>();
  const leanParts = new Map<number, { p0: Vec3; p1: Vec3; r: number } | null>();
  const hitVolumeT = (a: Vec3, b: Vec3, p: PlayerState): number | null => {
    if (measured) {
      let caps = capsules.get(p.id);
      if (!caps) { caps = measuredBody(p, s.planets, ps); capsules.set(p.id, caps); }
      let best: number | null = null;
      for (const c of caps) { const t = segmentCapsule(a, b, c.p0, c.p1, c.r); if (t !== null && (best === null || t < best)) best = t; }
      return best;
    }
    // 66.7: leaning out, the body at its spot and what shows past the edge (core/lean.ts leanPart)
    let part = leanParts.get(p.id);
    if (part === undefined) { part = p.cover ? leanPartOf(p, s.planets, ps) : null; leanParts.set(p.id, part); }
    const tPart = part ? segmentCapsule(a, b, part.p0, part.p1, part.r) : null;
    const tBody = bodyT(a, b, part ? { ...p, cover: undefined } : p);
    return tPart === null ? tBody : tBody === null || tPart < tBody ? tPart : tBody;
  };
  const bodyT = (a: Vec3, b: Vec3, p: PlayerState): number | null => {
    const shape = bodyStanceOf(p);   // (216: the old stance's for the first half of a change)
    if (!shape || shape === 'stand' || p.airborne) return segmentSphere(a, b, centreOf(p), ps.hitboxRadius);
    let parts = bodies.get(p.id);
    if (!parts) {
      const planet = s.planets[p.planetIndex];
      const n = vecNormalize(p.surfaceDir);
      const h = headingOf(p);
      parts = stanceSpheres(shape, ps).map(sp => {
        const foot = ps.fineAmplitude > 0 ? personPointAt(planet, vecAdd(n, vecScale(h, sp.along / planet.radius)), ps, 0) : vecAdd(getPlayerWorldPosition(p, s.planets), vecScale(h, sp.along));
        return { c: vecAdd(foot, vecScale(n, sp.up)), r: sp.r };
      });
      bodies.set(p.id, parts);
    }
    let best: number | null = null;
    for (const q of parts) { const t = segmentSphere(a, b, q.c, q.r); if (t !== null && (best === null || t < best)) best = t; }
    return best;
  };
  // 63.6: foliage slows and nudges rounds (the match's switch; core/foliage.ts) — not rockets, which fly on their motor
  const foliageOn = s.config?.foliageBullets === true;
  // 67.61: the jungle's understory and canopy too (core/jungleDepth.ts), with the jungle on — with or without the crowns' switch
  const jungleOn = (s.forest?.jungle ?? 0) > 0;   // (the held forest's share: as the field and the trees read it)
  const seedKey = foliageOn || jungleOn ? hashKey(String(s.config.seed ?? '')) : 0;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const drivesAny = s.structures.some(drives);
  // 65.22 (1): a gunner in any seat — a fixed gun's too (the HMG's) — fires out of its own structure: the round leaves the ring
  const mountedAny = drivesAny || s.players.some(p => p.mount);
  // Each person's ball (personBall: round its middle, every hit volume it has inside) is tested first, per person per
  // substep: a round that misses it misses the body, so the body's own test runs only near — one sphere a person, exact.
  // 65.28 (decision 161, GameConfig.suppression): the ball reaches SUPPRESSION.radiusPeople past the body (the sphere's
  // radius and that much more, never under the body's ball), and a round passing inside it marks an enemy suppressed
  const suppressing = defaultOn(s.config, 'suppression');
  const ballR = personBallRadius(ps);
  const nearR = suppressing ? Math.max(ballR, ps.hitboxRadius + SUPPRESSION.radiusPeople * person) : ballR;
  const middles = new Map<number, Vec3>();
  const middleOf = (p: PlayerState): Vec3 => { let c = middles.get(p.id); if (!c) { c = personBall(p, s.planets, ps).c; middles.set(p.id, c); } return c; };
  const near = new Set<number>();
  const anyDigs = ps.fineAmplitude > 0 && s.planets.some(p => p.digs?.length);   // 66.8: a dig's ground (a berm) stops a round, sampled finely near it
  const nearRounds: Array<{ unitId: number; point: Vec3 }> = [];   // (each round and each unit it passed: where it came closest — the flinch's cue)
  // 63.5 cover by material: the soil's wear, carried across the tick's rounds (core/groundWear.ts)
  let wear = state.groundWear, wearOwn = false;   // (copied once a tick, on the first bite: bumped in place after)
  for (const b0 of bullets) {
    const passed = suppressing ? new Set<number>() : null;
    // 63.1d step 3: a round from a vehicle's own gun (its shooter seated in it) leaves that hull; anyone else's meets it
    const ownHull = mountedAny ? s.players.find(p => p.id === b0.ownerId)?.mount?.bodyId : undefined;
    let pos = b0.pos, vel = b0.vel;
    const speed = vecLength(vel);
    const steps = Math.max(1, Math.ceil(speed * dt / ps.bulletSubstep));
    const h = dt / steps;
    // The tick's substep points first — the same arithmetic as the loop below (a handheld shot feels a person's
    // gravity, felt gravity §8.1) — so cover is looked up once for the tick, near these exact points (62.10)
    const pts: Vec3[] = new Array(steps), vels: Vec3[] = new Array(steps);
    // 65.30: the drag first in each substep (exact for the step: core/ballistics.ts), and the share of its speed it has left
    // after each — its damage follows it (the speed-share rule), as the foliage's and the ground's
    // 65.46: a rocket past its motor's burn coasts — its coast's fall and drag, no weathervane (decided at the tick's start)
    const coasting = !!b0.coast && b0.age >= (b0.life ?? ps.bulletLife);
    const drag = coasting ? b0.coast!.drag : b0.drag ?? 0, drag0 = b0.dragShare ?? 1;
    const keeps: number[] | null = drag > 0 ? new Array(steps) : null;
    {
      let p = pos, v = vel, kept = drag0;
      const fall = coasting ? b0.coast!.gravity : b0.gravity ?? ps.bulletGravityScale;   // S7: a rocket flies on its motor (0)
      // 62.52: the wind where the round is this tick (decision 120's switch; null without it): a bullet drifts with it, a rocket weathervanes into it
      const wind = s.config?.wind && !coasting ? actionWindAccel(s, nearPlanet(pos), pos, windShare(b0, s.config)) : null;   // (65.31: a motor weathervanes — the rocket; the 40 mm's lob its own share)
      for (let k = 0; k < steps; k++) {
        if (keeps) { const f = dragKeep(vecLength(v), drag, h); v = vecScale(v, f); kept *= f; keeps[k] = kept; }
        if (fall !== 0) v = vecAdd(v, vecScale(gravityAccel(p, s.planets), fall * ps.gravityScale * h));
        if (wind) v = vecAdd(v, vecScale(wind, h));
        const nx = vecAdd(p, vecScale(v, h));
        pts[k] = nx; vels[k] = v; p = nx;
      }
    }
    // 63.3 step 3: the shapes the tick's path could meet, per planet, looked up once (core/hits.ts)
    const coverNear = new Map<number, { shapes: PlanetShapes; near: number[] }>();
    const coverFor = (pi: number): { shapes: PlanetShapes; near: number[] } => {
      let c = coverNear.get(pi);
      if (!c) { const shapes = planetShapes(s, pi); c = { shapes, near: shapesNear([pos, ...pts], s.planets[pi], shapes, []) }; coverNear.set(pi, c); }
      return c;
    };
    // 63.6: the crowns the tick's path could cross, per planet, looked up once; the share of its muzzle speed it keeps,
    // and the small turns the trees it crosses give it (applied to its velocity at the tick's end)
    const leaves = foliageOn && !b0.blast ? new Map<number, { pf: PlanetFoliage; near: number[] }>() : null;
    const leavesFor = (pi: number) => {
      let l = leaves!.get(pi);
      if (!l) { l = { pf: planetFoliage(s, pi), near: foliageNear([pos, ...pts], pi, s) }; leaves!.set(pi, l); }
      return l;
    };
    const share0 = b0.speedShare ?? 1;
    let share = share0, crossed = false;
    // 63.5 cover by material (GROUND_BULLETS): a round into ground that lets rounds through marches on along its line to
    // where it comes out — within the material's `through` people, slowed by e^(−loss · depth) — or stops (null)
    const under = (q: Vec3) => (ps.fineAmplitude > 0 ? personGroundHit(q, s) ?? checkCollision(q, s.planets) : checkCollision(q, s.planets)) !== null;
    const groundPass = (from: Vec3, to: Vec3, at: { point: Vec3; planetIndex: number }): { exit: Vec3; keep: number; dir: Vec3 } | null => {
      const pl = s.planets[at.planetIndex];
      if (!pl || b0.blast) return null;   // a rocket bursts on the ground (and does not wear it)
      const dry = groundMaterial(s, at.planetIndex, vecNormalize(vecSub(at.point, pl.position)), seconds);
      // soil takes the round's bite (its cell's wear one more); worn past GROUND_WEAR.hitsToPass it passes rounds as sand
      let mat = dry;
      if (dry === 'soil') {
        const id = wearId(s, at.planetIndex, at.point);
        if (wearCount(wear, id) >= GROUND_WEAR.hitsToPass) mat = 'sand';
        if (!wearOwn) { wear = (wear ?? []).slice(); wearOwn = true; }
        bumpWear(wear!, id); simWork.groundWearBumps++;
      }
      const g = GROUND_BULLETS[mat];
      if (!(g.through > 0)) return null;
      const d = vecSub(to, from), L = vecLength(d);
      if (!(L > 0)) return null;
      const u = vecScale(d, 1 / L), step = 0.1 * person;
      let entry = L;
      for (let x = step; x < L; x += step) if (under(vecAdd(from, vecScale(u, x)))) { entry = x; break; }
      for (let x = entry + step; x <= entry + g.through * person + 1e-12; x += step) {
        const q = vecAdd(from, vecScale(u, x));
        if (!under(q)) return { exit: q, keep: dexp(-g.loss * (x - entry) / person), dir: u };
      }
      return null;
    };
    const turns: Array<{ angle: number; phi: number }> = [];
    let done = false;
    // 130 (d), the owner: "Everything follows the setting" — with friendly fire off a bullet passes a teammate by (as the
    // beam does), and in between it hurts a teammate by the setting's share (as a blast does)
    const shooterTeam = s.players.find(q => q.id === b0.ownerId)?.teamIndex;
    const friendly = friendlyShare(s.config);
    for (let k = 0; k < steps && !done; k++) {
      vel = vels[k];
      const next = pts[k];
      // Units first along the segment (a unit standing in front of a ridge is hit before the ridge)
      let best: { t: number; target: PlayerState } | null = null;
      let nearHere: Array<[number, number]> | undefined;   // 65.28: the enemies this substep passes near, and where along it
      for (const p of s.players) {
        if (!p.alive || p.id === b0.ownerId || (p.landingLeft ?? 0) > 0 || p.boarded) continue;   // (69.3c: in its pod, not a target; 208: nor one in the wave ship)
        if (friendly <= 0 && p.teamIndex === shooterTeam) continue;
        const tn = segmentSphere(pos, next, middleOf(p), nearR);
        if (tn === null) continue;   // (missed the ball: missed the body)
        if (suppressing && p.teamIndex !== shooterTeam) (nearHere ??= []).push([p.id, tn]);
        const t = hitVolumeT(pos, next, p);
        if (t !== null && (!best || t < best.t)) best = { t, target: p };
      }
      // I2: an enemy drone in the way comes down (one hit), if it is nearer along the segment than any unit
      let drone: { t: number; id: number; carrier?: true } | null = null;
      for (const d of s.drones ?? []) {
        if (!d.alive || d.teamIndex === shooterTeam) continue;
        const t = segmentSphere(pos, next, dronePos(d, s), ps.droneHitRadius);
        if (t !== null && (!drone || t < drone.t)) drone = { t, id: d.id };
      }
      // 69.32 step 3: an enemy supply drone in the way takes the round (still up: one downed earlier this step is gone)
      for (const x of carriers) {
        if (x.run.team === shooterTeam || !s.supplyDrones?.runs.some(r => r.id === x.run.id)) continue;
        const t = segmentSphere(pos, next, x.pos, carrierR);
        if (t !== null && (!drone || t < drone.t)) drone = { t, id: x.run.id, carrier: true };
      }
      // S3: at person scale a bullet meets the fine ground (a hummock stops it); the mesh alone at classic
      const ground = ps.fineAmplitude > 0 ? personGroundHit(next, s) ?? checkCollision(next, s.planets) : checkCollision(next, s.planets);
      // 63.3 step 3: cover along the whole substep, exactly — it stops the shot only if it comes before the person or
      // the drone the substep also meets (it used to be one point at the substep's end, tested only when nothing else was)
      const pi = nearPlanet(next);
      const c = pi >= 0 ? coverFor(pi) : null;
      // (65.22 (1): a fixed gun's own shape — the HMG's sandbag ring — is not in its gunner's way: the gun fires over it, its
      // muzzle at the parapet; it was, so the .50 could not shoot down past about 6° below level but through the gap)
      const nearOwn = ownHull !== undefined && c ? c.near.filter(i => !(c.shapes.list[i].ref.kind === 'structure' && c.shapes.list[i].ref.id === ownHull)) : c?.near;
      const still = c && nearOwn && nearOwn.length ? firstHit(pos, next, c.shapes, nearOwn, s.planets[pi]) : null;
      const rolling = pi >= 0 ? firstMovingHit(pos, next, pi, s, ownHull) : null;   // 63.1d step 2: a hull that drives, where it stands (step 3: not the shooter's own)
      const cover0 = rolling && (!still || rolling.t < still.t) ? rolling : still;
      // 69.3b, the ship as a body: a ship across the substep stops the round where it strikes (the hull wears nothing from a round)
      const shipHit = pi >= 0 && s.config.shipsDestructible ? firstShipHit(pos, next, pi, s, b0.fromShip) : null;   // (69.59 step 3: not the ship it was fired from)
      const cover = shipHit && (!cover0 || shipHit.t < cover0.t) ? { t: shipHit.t, ref: null, ship: shipHit.id } : cover0;
      // 63.6: the foliage it crosses before whatever it meets in this substep slows it (so the hit takes the slower round's
      // damage) and turns it a little — never under the floor, so a stand never blocks
      if (leaves && pi >= 0) {
        const l = leavesFor(pi);
        if (l.near.length) {
          const tEnd = Math.min(cover ? cover.t : 1, drone ? drone.t : 1, best ? best.t : 1);
          const end = tEnd < 1 ? vecAdd(pos, vecScale(vecSub(next, pos), tEnd)) : next;
          for (const x of foliageCrossed(pos, end, l.pf, l.near)) {
            const depth = x.depth / person;
            share = Math.max(FOLIAGE_BULLETS.floor, share * dexp(-FOLIAGE_BULLETS.lossPerPerson * x.density * depth));
            turns.push({ angle: FOLIAGE_BULLETS.deflectPerPerson * x.density * depth * keyedRandom(seedKey, b0.id, x.id, 1), phi: 2 * Math.PI * keyedRandom(seedKey, b0.id, x.id, 2) });
          }
        }
      }
      if (jungleOn && !b0.blast && pi >= 0) {
        const tEnd = Math.min(cover ? cover.t : 1, drone ? drone.t : 1, best ? best.t : 1);
        const end = tEnd < 1 ? vecAdd(pos, vecScale(vecSub(next, pos), tEnd)) : next;
        for (const x of jungleCrossed(s, pi, pos, end)) {
          const depth = x.depth / person;   // (depth × density already)
          share = Math.max(FOLIAGE_BULLETS.floor, share * dexp(-FOLIAGE_BULLETS.lossPerPerson * depth));
          turns.push({ angle: FOLIAGE_BULLETS.deflectPerPerson * depth * keyedRandom(seedKey, b0.id, JUNGLE_KEY + x.cell, 1), phi: 2 * Math.PI * keyedRandom(seedKey, b0.id, JUNGLE_KEY + x.cell, 2) });
        }
      }
      // 66.8: a dig's ground across the substep (a berm, a far wall) — before whatever else it would meet there
      const dug = anyDigs ? digSegmentHit(pos, next, s.planets, ps) : null;
      const dugFirst = !!dug && (!cover || dug.t < cover.t) && (!best || dug.t < best.t) && (!drone || dug.t < drone.t);
      // 65.28: a round passes near those it reaches before it stops (a prop, a drone or a body in the way first)
      if (nearHere) {
        const stopT = dugFirst ? dug!.t : cover && (!best || cover.t < best.t) && (!drone || cover.t < drone.t) ? cover.t : drone && (!best || drone.t < best.t) ? drone.t : best ? best.t : 1;
        for (const [id, tn] of nearHere) {
          if (tn > stopT) continue;
          near.add(id);
          // the cue once a round: where it enters the unit's ball (a round already inside entered it in an earlier substep),
          // or the substep it is fired in
          if (passed!.has(id) || !(tn > 0 || (k === 0 && b0.age === 0))) continue;
          passed!.add(id);
          const c = middleOf(s.players.find(q => q.id === id)!), d = vecSub(next, pos), l2 = vecDot(d, d);
          const t = l2 > 0 ? Math.max(0, Math.min(stopT, vecDot(vecSub(c, pos), d) / l2)) : 0;
          nearRounds.push({ unitId: id, point: vecAdd(pos, vecScale(d, t)) });
        }
      }
      if (dugFirst) {
        events.push({ kind: 'hitGround', bullet: { ...b0, pos: dug!.point, vel }, point: dug!.point, planetIndex: dug!.planetIndex });
        done = true;
      } else if (cover && (!best || cover.t < best.t) && (!drone || cover.t < drone.t)) {
        const point = vecAdd(pos, vecScale(vecSub(next, pos), cover.t));
        // 63.1o: a round into an unarmoured vehicle (the technical: soft) wears it at the damage table's share — any other
        // structure takes nothing from a round (its share 0), so is left as it was
        if (cover.ref === null) s = shipBlow(s, cover.ship, pi, point, b0.damage * share * (keeps ? keeps[k] : 1), 'bullet');   // (69.3b: through the table's hull row — nothing)
        if (cover.ref?.kind === 'structure') {
          const hitId = cover.ref.id, st = s.structures.find(x => x.id === hitId);
          if (st && st.health > 0 && layoutFor(st, s.config)) s = moduleBlow(s, st.id, point, b0.damage * share * (keeps ? keeps[k] : 1), 'bullet');   // 63.14 step 4: the module the round struck wears
          // 63.12 (vehicleZones): through the zone it struck, the occupant of the seat behind it is hit as a person (a driver shot
          // through his door's glass); through and no one there, a hull no round wears takes the soft row's share
          const zh = st && st.health > 0 && zonesOn(s.config) ? zoneOf(s, st, point, false, vel) : null;
          const pen = b0.pen ?? ROUND_PENETRATION[b0.round ?? '7.62'] ?? 1;   // (69.29: a round's own penetration first)
          const through = !!zh && pen >= zh.effective;   // (69.29 step 4: at the angle its flight meets the face)
          // 69.29: the tank's solid round strikes a hull whole — its blow the hull's as a burst's (any material), through the zone
          // it struck or, stopped, its dented share; a cannon's kill, not gunfire's (it bursts at once)
          if (st && st.health > 0 && b0.solid) {
            const r = damageStructure(st, b0.damage * share * (keeps ? keeps[k] : 1) * (zh ? hullShare(zoneTableOf(st)!.cls, pen, zh.effective) : 1), 'blast');
            // 69.29 step 4 (DECISIONS 245 (3)): through its running gear or its engine, a mobility kill
            const knocked = through && zh ? knockedOut(zh.zone, zh.local) : null;
            const hit = knocked && !r.destroyed ? { ...r.structure, disabled: { ...(r.structure.disabled ?? {}), ...knocked } } : r.structure;
            s = { ...s, structures: s.structures.map(x => (x.id === st.id ? hit : x)) };
          }
          if (st && zh && through) {
            const amount = b0.damage * share * (keeps ? keeps[k] : 1), seat = seatBehind(zoneTableOf(st)!, zh.zone, zh.local);
            const occ = seat >= 0 ? s.players.find(q => q.id === seatUnit(st.seats?.[seat]) && q.alive) : undefined;
            if (occ) {
              let dealt = occ.teamIndex === shooterTeam ? amount * friendly : amount;
              if (occ.armour) {
                const t = throughArmour(occ, 'body', b0.round, dealt);
                if (t.unit !== occ) { const worn = t.unit; s = { ...s, players: s.players.map(q => (q.id === worn.id ? { ...q, armour: worn.armour } : q)) }; }
                dealt = t.amount;
              }
              const r = applyHitscanDamage(s, b0.ownerId, occ.id, dealt);
              s = r.state;
              events.push({ kind: 'hitUnit', bullet: { ...b0, pos: point, vel }, targetId: occ.id, point, damage: dealt, killed: r.killed });
            } else if (structureMaterial(st) === 'hull' && !b0.solid) {
              const r = damageStructure(st, amount * ZONE_RULES.hullRound, 'blast');
              const hurt = r.destroyed ? { ...r.structure, gunfireKill: true as const } : r.structure;
              s = { ...s, structures: s.structures.map(x => (x.id === st.id ? hurt : x)) };
            }
          }
          if (st && st.health > 0 && structureMaterial(st) === 'soft' && !b0.solid) s = applyDamage(s, { structure: st.id }, b0.damage * share * (keeps ? keeps[k] : 1), 'bullet', b0.ownerId).state;   // (65.30: by the speed it arrives with, as a person's)
        }
        events.push({ kind: 'hitCover', bullet: { ...b0, pos: point, vel }, point, planetIndex: pi, ...(cover.ref ? { hit: cover.ref } : {}) });
        done = true;
      } else if (drone && (!best || drone.t < best.t)) {
        const point = vecAdd(pos, vecScale(vecSub(next, pos), drone.t));
        if (drone.carrier) {
          s = hitSupplyDrone(s, drone.id, b0.damage, tick);   // (69.32 step 3: a round's whole damage — two bring it down)
          events.push({ kind: 'hitSupplyDrone', bullet: { ...b0, pos: point, vel }, runId: drone.id, point });
        } else {
          s = applyDamage(s, { drone: drone.id }, b0.damage, 'bullet', b0.ownerId).state;   // 63.4 step 5: through the door (65.42: its DRONE_HITS-th downs it)
          events.push({ kind: 'hitDrone', bullet: { ...b0, pos: point, vel }, droneId: drone.id, point });
        }
        done = true;
      } else if (best) {
        const point = vecAdd(pos, vecScale(vecSub(next, pos), best.t));
        const base = b0.damage * share * (keeps ? keeps[k] : 1);   // 63.6: the damage follows the speed it arrives with (the foliage's, 63.5 the ground's, 65.30 the drag's)
        let damage = best.target.teamIndex === shooterTeam ? base * friendly : base;
        // 65.27: where it landed (head / body / limbs) × its multiplier; 185: what the armour there stops, worn by it
        const loc = defaultOn(s.config, 'hitLocations') ? hitLocation(best.target, point, s.planets, ps, measured) : null;
        if (loc) damage *= locationMultiplier(s.config, loc);
        if (best.target.armour) {
          const t = throughArmour(best.target, loc ?? 'body', b0.round, damage);
          if (t.unit !== best.target) { const worn = t.unit; s = { ...s, players: s.players.map(q => (q.id === worn.id ? { ...q, armour: worn.armour } : q)) }; }
          damage = t.amount;
        }
        const r = applyHitscanDamage(s, b0.ownerId, best.target.id, damage);
        s = r.state;
        events.push({ kind: 'hitUnit', bullet: { ...b0, pos: point, vel }, targetId: best.target.id, point, damage, killed: r.killed, ...(loc ? { location: loc } : {}) });
        done = true;
      } else if (ground) {
        const pass = groundPass(pos, next, ground);
        if (pass && share * pass.keep >= GROUND_BULLET_FLOOR) {
          // through the berm: on from where it comes out, slowed; the substeps it passed inside the ground are skipped
          share *= pass.keep; crossed = true; pos = pass.exit;
          while (k + 1 < steps && vecDot(vecSub(pts[k + 1], pass.exit), pass.dir) <= 0) k++;
        } else {
          events.push({ kind: 'hitGround', bullet: { ...b0, pos: ground.point, vel }, point: ground.point, planetIndex: ground.planetIndex });
          done = true;
        }
      } else {
        pos = next;
      }
    }
    if (done) continue;
    const age = b0.age + dt;
    // 63.6: the speed it kept and the turns it took through the foliage, on its velocity from here on
    if ((leaves || crossed) && (share !== share0 || turns.length)) {
      const sp = vecLength(vel);
      let dir = vecScale(vel, 1 / sp);
      for (const tn of turns) {
        const e1 = vecNormalize(Math.abs(dir.y) < 0.9 ? vecCross(dir, { x: 0, y: 1, z: 0 }) : vecCross(dir, { x: 1, y: 0, z: 0 })), e2 = vecCross(dir, e1);
        dir = vecNormalize(vecAdd(dir, vecScale(vecAdd(vecScale(e1, dcos(tn.phi)), vecScale(e2, dsin(tn.phi))), tn.angle)));
      }
      vel = vecScale(dir, sp * (share / share0));
    }
    const kept: { speedShare?: number; dragShare?: number } = (leaves || crossed || b0.speedShare !== undefined) && share < 1 ? { speedShare: share } : {};
    if (keeps) kept.dragShare = keeps[steps - 1];
    if (age > (b0.life ?? ps.bulletLife) + (b0.coast?.seconds ?? 0) || vecLength(pos) > OUT_OF_BOUNDS) { events.push({ kind: 'expired', bullet: { ...b0, pos, vel, age, ...kept } }); continue; }
    alive.push({ ...b0, pos, vel, age, ...kept });
  }
  return { state: { ...s, bullets: alive, ...(wear !== state.groundWear ? { groundWear: wear } : {}) }, events, ...(suppressing ? { near: [...near].sort((a, b) => a - b), nearRounds } : {}) };
}
